import type { ConfigScope, TargetDTO, VpsDTO } from '@devrobot/shared';

const HINT: Record<ConfigScope['kind'], (env: string) => string> = {
  environment: (env) => `Used by every deployment of ${env} unless a more specific override applies.`,
  vps: (env) => `Overrides the ${env} value for deployments of this project on the selected VPS only.`,
  target: () => 'Overrides the value for one deployment target only (highest precedence).',
  personal: () => 'Only for your own local devrobot dev / devrobot run. Never deployed and never visible to others.',
};

/** Where a value is written: environment, VPS override, target override, or personal (development only). */
export function ScopePicker({ env, value, onChange, vpsList, targets }: { env: string; value: ConfigScope; onChange: (s: ConfigScope) => void; vpsList: VpsDTO[]; targets: TargetDTO[] }) {
  const envTargets = targets.filter((t) => t.environment === env && !t.removedAt);
  const setKind = (kind: ConfigScope['kind']) => {
    if (kind === 'environment' || kind === 'personal') onChange({ kind });
    else if (kind === 'vps') onChange({ kind, vpsId: vpsList[0]?.id ?? '' });
    else onChange({ kind, targetId: envTargets[0]?.id ?? '' });
  };
  return (
    <fieldset className="fieldset">
      <legend className="fieldset-legend">Scope</legend>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        <select className="select w-full" value={value.kind} onChange={(e) => setKind(e.target.value as ConfigScope['kind'])} aria-label="Scope">
          <option value="environment">Environment ({env})</option>
          <option value="vps" disabled={vpsList.length === 0}>
            VPS override
          </option>
          <option value="target" disabled={envTargets.length === 0}>
            Target override
          </option>
          {env === 'development' ? <option value="personal">Personal override</option> : null}
        </select>
        {value.kind === 'vps' ? (
          <select className="select w-full animate-pop" value={value.vpsId} onChange={(e) => onChange({ kind: 'vps', vpsId: e.target.value })} aria-label="VPS">
            {vpsList.map((v) => (
              <option key={v.id} value={v.id}>
                {v.name}
              </option>
            ))}
          </select>
        ) : null}
        {value.kind === 'target' ? (
          <select className="select w-full animate-pop" value={value.targetId} onChange={(e) => onChange({ kind: 'target', targetId: e.target.value })} aria-label="Deployment target">
            {envTargets.map((t) => (
              <option key={t.id} value={t.id}>
                {t.stack} on {t.vps.name}
              </option>
            ))}
          </select>
        ) : null}
      </div>
      <p className="label whitespace-normal text-base-content/60">{HINT[value.kind](env)}</p>
    </fieldset>
  );
}

export function scopeValid(s: ConfigScope): boolean {
  return s.kind === 'vps' ? Boolean(s.vpsId) : s.kind === 'target' ? Boolean(s.targetId) : true;
}
